import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIsFetching } from 'react-query';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { FullPageLoading } from '../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { VisualPicker } from '../../../../../CommonComponents/OPL/VisualPicker/VisualPicker';
import { formatCurrency } from '../../../../../CommonUtils/formatNumber';
import {
  AddAccountSelectionModalWrapper,
  Selection,
} from '../../../../../Profile/Accounts/components/AddAccountSelectionModalWrapper/AddAccountSelectionModalWrapper';
import { AccountModalUsageContext } from '../../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/context/AccountFormProvider.meta';
import { queryKeys } from '../../api/queryKeys';
import { AccountDTO, useAccountsQuery } from '../../api/useAccountsQuery';
import { WorkflowDTO } from '../../api/useCharitableGivingQuery';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { StepErrorState } from '../../components/StepErrorState/StepErrorState';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { StepLoading } from '../../components/StepLoading/StepLoading';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { useNavigation } from '../../hooks/useNavigation';
import { FormValues, STEP_ID, validation } from './FundAccounts.meta';

export const FundAccounts = () => {
  const {
    workflow: {
      data: { givingStrategy, donor, kindOfProperty },
    },
  } = useCharitableGivingContext();

  const { isLoading, isError, refetch, data } = useAccountsQuery({
    givingStrategy,
    donor,
    kindOfProperty,
  });

  if (isLoading) {
    return <StepLoading />;
  }

  if (isError) {
    return <StepErrorState onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  return <Content accounts={data} />;
};

interface ContentProps {
  accounts: AccountDTO[];
}

const Content = ({ accounts }: ContentProps) => {
  const isFetchingAccounts = useIsFetching(queryKeys.accountsAll()) > 0;

  const { workflow, setWorkflow, setStep } = useCharitableGivingContext();

  const { progress, previousStep, nextStep, isFirstStep } = useNavigation({ currentStep: STEP_ID });

  const [isOpenAddAccountModal, setIsOpenAddAccountModal] = useState(false);

  const validAccounts = accounts.reduce((acc, account) => {
    if (workflow.data.fundAccounts?.includes(account.id)) {
      return [...acc, account.id];
    }

    return acc;
  }, [] as string[]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      fundAccounts: validAccounts,
    },
    resolver: yupResolver(validation),
  });

  /**
   * Effect to allow auto-selecting newly added accounts
   */
  const fundAccountsRef = useRef(accounts.map(({ id }) => id));
  useEffect(() => {
    if (accounts.length !== fundAccountsRef.current.length) {
      const newFundAccounts = accounts.reduce((acc, { id }) => {
        if (!fundAccountsRef.current.includes(id)) {
          acc.push(id);
        }

        return acc;
      }, [] as string[]);

      const currentFundAccounts = getValues('fundAccounts');

      setValue('fundAccounts', [...currentFundAccounts, ...newFundAccounts]);

      fundAccountsRef.current = accounts.map(({ id }) => id);
    }
  }, [accounts, getValues, setValue]);

  const onSubmit = handleSubmit(values => {
    const newWorkflow: WorkflowDTO = {
      ...workflow,
      data: {
        ...workflow.data,
        fundAccounts: values.fundAccounts,
      },
    };

    setWorkflow(newWorkflow);

    if (nextStep) {
      setStep(nextStep);
    }
  });

  const AddAccountButton = (
    <Button variant="link" leftIcon="fa fa-plus" onClick={() => setIsOpenAddAccountModal(true)}>
      Add Account
    </Button>
  );

  return (
    <>
      {isFetchingAccounts && <FullPageLoading />}
      <StepContainer
        alert={(errors.fundAccounts as any)?.message}
        title={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text color="neutral.700" fontSize="h3" fontWeight={500}>
              {workflow.data.givingStrategy === 0
                ? 'Which account(s) will fund the DAF?'
                : 'Which account(s) will fund this goal?'}
            </Text>

            {accounts.length > 0 && AddAccountButton}
          </Box>
        }
        onSubmit={onSubmit}
        footer={
          <StepFooter
            isFirstStep={isFirstStep}
            progress={progress}
            onPrevious={previousStep ? () => setStep(previousStep) : undefined}
          />
        }
      >
        {accounts.length === 0 && (
          <Box
            display="grid"
            style={{ placeItems: 'center' }}
            borderTop="1px solid"
            borderColor="neutral.200"
            pt="32"
            mx={-32}
          >
            {AddAccountButton}
          </Box>
        )}

        {accounts.length > 0 && (
          <Controller
            control={control}
            name="fundAccounts"
            render={({ field: { value: fundAccounts } }) => (
              <Box display="flex" flexDirection="column" mt="16" css="gap: 16px">
                {accounts.map(({ id, name, balance, institution }) => (
                  <VisualPicker
                    key={id}
                    variant="radio"
                    label={name}
                    helperText={institution ?? undefined}
                    rightText={formatCurrency(balance)}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();

                      const {
                        currentTarget: { checked },
                      } = event;

                      if (!checked) {
                        return setValue('fundAccounts', [...fundAccounts, id]);
                      }

                      return setValue(
                        'fundAccounts',
                        fundAccounts.filter(accountId => accountId !== id),
                      );
                    }}
                    checked={fundAccounts.includes(id)}
                  />
                ))}
              </Box>
            )}
          />
        )}
      </StepContainer>

      {isOpenAddAccountModal && (
        <AddAccountSelectionModalWrapper
          initialSelection={Selection.Manual}
          usageContext={AccountModalUsageContext.CharitableGivingWorkflow}
          usageContextOptions={{
            CharitableGivingDonor: workflow.data.donor,
            CharitableGivingStrategy: workflow.data.givingStrategy,
            CharitableGivingKindOfProperty: workflow.data.kindOfProperty,
          }}
          showExternalProviders={false}
          onClose={() => setIsOpenAddAccountModal(false)}
        />
      )}
    </>
  );
};
